import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Text,
    Card,
    CardBody,
    Select,
    Heading,
    Grid,
    GridItem,
    VStack,
    HStack,
    Spinner,
    Tag,
    Center,
} from '@chakra-ui/react';
import {
    FiSearch,
    FiFilter,
    FiClock,
    FiUser,
    FiUserCheck,
    FiEye,
    FiMousePointer,
    FiLogIn,
    FiShoppingCart,
    FiLogOut,
    FiAlertCircle,
    FiCheckCircle,
    FiXCircle,
    FiActivity,
    FiPlayCircle,
    FiStopCircle,
} from 'react-icons/fi';
import { MdTimer } from 'react-icons/md';
import Pagination from 'react-js-pagination';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import Header from '../../components/Header';
import API from '../../helpers/axiosInstance';
import { getApiHeaders } from '../../utils/DefaultFunction';
import usePagePermissions from '../../hooks/usePagePermissions';

// ─── Debounce Hook ───────────────────────────────────────────────────────────
function useDebounce(value, delay = 500) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);

    return debouncedValue;
}

// ─── Event Color Map ─────────────────────────────────────────────────────────
const EVENT_COLOR_PALETTE = [
    { bg: 'blue.50', border: 'blue.200', text: 'blue.800', iconBg: 'blue.100' },
    { bg: 'purple.50', border: 'purple.200', text: 'purple.800', iconBg: 'purple.100' },
    { bg: 'green.50', border: 'green.200', text: 'green.800', iconBg: 'green.100' },
    { bg: 'orange.50', border: 'orange.200', text: 'orange.800', iconBg: 'orange.100' },
    { bg: 'pink.50', border: 'pink.200', text: 'pink.800', iconBg: 'pink.100' },
    { bg: 'teal.50', border: 'teal.200', text: 'teal.800', iconBg: 'teal.100' },
    { bg: 'cyan.50', border: 'cyan.200', text: 'cyan.800', iconBg: 'cyan.100' },
    { bg: 'red.50', border: 'red.200', text: 'red.800', iconBg: 'red.100' },
    { bg: 'yellow.50', border: 'yellow.200', text: 'yellow.800', iconBg: 'yellow.100' },
];

const generateEventColor = (eventName) => {
    let hash = 0;
    for (let i = 0; i < eventName.length; i++) {
        hash = eventName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return EVENT_COLOR_PALETTE[Math.abs(hash) % EVENT_COLOR_PALETTE.length];
};

// ─── Session Card ────────────────────────────────────────────────────────────
const SessionCard = ({ session, isSelected, onSelect, getStatusIcon }) => (
    <Card
        cursor="pointer"
        transition="all 0.2s"
        borderWidth="1px"
        borderColor={isSelected ? 'var(--brand-primary)' : 'gray.200'}
        bg={isSelected ? 'blue.50' : 'white'}
        boxShadow={isSelected ? 'md' : 'sm'}
        _hover={{ shadow: 'md', borderColor: isSelected ? 'var(--brand-primary)' : 'gray.300' }}
        onClick={onSelect}
    >
        <CardBody p={4}>
            <Flex justify="space-between" align="start" mb={2}>
                <Text as="code" fontSize="xs" color="gray.500" fontFamily="mono">
                    {session.session_id}
                </Text>
                {getStatusIcon(session.status)}
            </Flex>

            {session.user?.is_known ? (
                <>
                    <Text fontWeight="semi-bold" mb={0.5}>{session.contact}</Text>
                    <Text color="gray.600" mb={2}>{session.user.name}</Text>
                    <Tag colorScheme="green" display="flex" alignItems="center" width="fit-content">
                        <Box as={FiUserCheck} boxSize={3} mr={1} />
                        Known User
                    </Tag>
                </>
            ) : (
                <>
                    <Text fontFamily="mono" color="gray.600" mb={2}>{session.contact}</Text>
                    <Tag colorScheme="gray" display="flex" alignItems="center" width="fit-content">
                        <Box as={FiUser} boxSize={3} mr={1} />
                        Anonymous User
                    </Tag>
                </>
            )}

            <HStack mt={3} fontSize="xs" color="gray.500">
                <Box as={FiClock} boxSize={3} />
                <Text>{new Date(session.started_at).toLocaleString()}</Text>
            </HStack>
        </CardBody>
    </Card>
);

// ─── Session List (Left Sidebar) ─────────────────────────────────────────────
const SessionList = ({
    sessions,
    selectedSession,
    onSelectSession,
    searchQuery,
    onSearchChange,
    userTypeFilter,
    onUserTypeChange,
    timeFilter,
    onTimeFilterChange,
    channelFilter,
    onChannelFilterChange,
    getStatusIcon,
    loading,
    pagination,
    onPageChange,
}) => (
    <Box
        w="320px"
        flexShrink={0}
        bg="white"
        border="1px"
        borderColor="gray.200"
        borderRadius="lg"
        boxShadow="sm"
        overflow="hidden"
        display="flex"
        flexDirection="column"
    >
        {/* Filters */}
        <VStack p={4} borderBottom="1px" borderColor="gray.200" spacing={3} align="stretch">
            <InputGroup>
                <InputLeftElement pointerEvents="none">
                    <Box as={FiSearch} color="gray.400" boxSize={4} />
                </InputLeftElement>
                <Input
                    placeholder="Search by session ID or contact..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                />
            </InputGroup>

            <HStack spacing={2}>
                {['all', 'known', 'anonymous'].map((type) => (
                    <Button
                        key={type}
                        size="sm"
                        variant={userTypeFilter === type ? 'solid' : 'outline'}
                        colorScheme={userTypeFilter === type ? 'blue' : 'gray'}
                        bg={userTypeFilter === type ? 'var(--brand-primary)' : undefined}
                        _hover={userTypeFilter === type ? { bg: 'var(--brand-primary-hover)' } : undefined}
                        onClick={() => onUserTypeChange(type)}
                        textTransform="capitalize"
                    >
                        {type === 'all' ? 'All' : type === 'known' ? 'Known' : 'Anonymous'}
                    </Button>
                ))}
            </HStack>
            <HStack spacing={2}>
                {['all', 'App', 'Website', "API"].map((channel) => (
                    <Button
                        key={channel}
                        size="sm"
                        variant={channelFilter === channel ? 'solid' : 'outline'}
                        colorScheme={channelFilter === channel ? 'blue' : 'gray'}
                        bg={channelFilter === channel ? 'var(--brand-primary)' : undefined}
                        _hover={channelFilter === channel ? { bg: 'var(--brand-primary-hover)' } : undefined}
                        onClick={() => onChannelFilterChange(channel)}
                    >
                        {channel === 'all' ? 'All' : channel}
                    </Button>
                ))}
            </HStack>

            <Select value={timeFilter} onChange={(e) => onTimeFilterChange(e.target.value)}>
                <option value="1">Last 1 hour</option>
                <option value="24">Last 24 hours</option>
                <option value="168">Last 7 days</option>

                <option value="360">Last 15 days</option>
                <option value="720">Last 30 days</option>
                <option value="1440">Last 60 days</option>
                <option value="2160">Last 90 days</option>
                <option value="4320">Last 180 days</option>

                <option value="all">All Time</option>
            </Select>

            {/* Channel Filter */}

        </VStack>

        {/* Session List */}
        <Box flex={1} overflowY="auto">
            {loading ? (
                <VStack py={12} spacing={3}>
                    <Spinner size="xl" color="var(--brand-primary)" />
                    <Text color="gray.500">Loading sessions...</Text>
                </VStack>
            ) : sessions.length === 0 ? (
                <VStack py={12} spacing={3}>
                    <Box as={FiFilter} boxSize={12} color="gray.300" />
                    <Text color="gray.500" fontWeight="medium">No sessions found</Text>
                    <Text color="gray.400">Try adjusting your filters</Text>
                </VStack>
            ) : (
                <VStack p={3} spacing={2} align="stretch">
                    {sessions.map((session) => (
                        <SessionCard
                            key={session.session_id}
                            session={session}
                            isSelected={selectedSession?.session_id === session.session_id}
                            onSelect={() => {
                                onSelectSession(session);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            getStatusIcon={getStatusIcon}
                        />
                    ))}
                </VStack>
            )}
        </Box>

        {/* Pagination */}
        {!loading && sessions.length > 0 && pagination && (
            <Box p={4} borderTop="1px" borderColor="gray.200">
                <Pagination
                    activePage={pagination.page}
                    itemsCountPerPage={pagination.limit}
                    totalItemsCount={pagination.total}
                    pageRangeDisplayed={5}
                    onChange={onPageChange}
                    itemClass="page-item"
                    linkClass="page-link"
                    activeClass="active"
                />
            </Box>
        )}
    </Box>
);

// ─── Event Timeline Card (Updated UI) ───────────────────────────────────────
const EventTimelineCard = ({
    event,
    index,
    totalEvents,
    isHovered,
    onHover,
    onHoverEnd,
    getEventIcon,
    colors,
}) => {
    const EventIcon = getEventIcon(event.event || event.eventType);
    const eventLabel = event.label || event.pageName || '';
    const eventTime = new Date(event.timestamp).toLocaleTimeString();
    const isFirst = index === 0;
    const isLast = index === totalEvents - 1;

    return (
        <Box position="relative">
            {/* Vertical connector line */}
            {index < totalEvents - 1 && (
                <Box
                    position="absolute"
                    left="19px"
                    top="40px"
                    bottom="-16px"
                    w="2px"
                    bg="gray.200"
                    zIndex={0}
                />
            )}

            <Card
                borderWidth="1px"
                borderColor={colors.border}
                bg={colors.bg}
                transition="all 0.2s"
                _hover={{ shadow: 'lg' }}
                cursor="pointer"
                onMouseEnter={onHover}
                onMouseLeave={onHoverEnd}
            >
                <CardBody p={3}>
                    <HStack spacing={3} align="center">
                        {/* Icon circle */}
                        <Flex
                            w={8}
                            h={8}
                            borderRadius="full"
                            bg="white"
                            borderWidth="2px"
                            borderColor={colors.border}
                            align="center"
                            justify="center"
                            flexShrink={0}
                            position="relative"
                            zIndex={10}
                        >
                            <Box as={EventIcon} boxSize={4} color={colors.text} />
                        </Flex>

                        {/* Content */}
                        <Box flex={1} minW={0}>
                            <Flex justify="space-between" align="center" gap={3}>
                                <Text fontWeight="500" isTruncated color={colors.text}>
                                    {event.event}
                                </Text>
                                <HStack spacing={2} flexShrink={0}>
                                    <Text fontSize="xs" color="gray.500">
                                        {eventTime}
                                    </Text>
                                    {event.time_spent > 0 && (
                                        <Tag
                                            size="sm"
                                            variant="outline"
                                            borderColor={colors.border}
                                            color={colors.text}
                                        >
                                            <Box as={MdTimer} boxSize={3} mr={1} />
                                            {event.time_spent}s
                                        </Tag>
                                    )}
                                </HStack>
                            </Flex>

                            {/* Properties on hover */}
                            {isHovered && event.properties && (
                                <Card
                                    mt={2}
                                    bg="whiteAlpha.800"
                                    borderStyle="dashed"
                                    borderColor={colors.border}
                                    borderWidth="1px"
                                >
                                    <CardBody p={2}>
                                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
                                            Event Payload:
                                        </Text>
                                        <Box
                                            as="pre"
                                            fontSize="xs"
                                            fontFamily="mono"
                                            color="gray.700"
                                            overflowX="auto"
                                            overflowY="auto"
                                            maxH="300px"
                                            whiteSpace="pre-wrap"
                                            wordBreak="break-word"
                                        >
                                            {JSON.stringify(event.properties, null, 2)}
                                        </Box>
                                    </CardBody>
                                </Card>
                            )}
                        </Box>
                    </HStack>
                </CardBody>
            </Card>
        </Box>
    );
};

// ─── Session Details (Right Panel) ───────────────────────────────────────────
const SessionDetails = ({
    session,
    timeline,
    loading,
    hoveredEvent,
    onEventHover,
    getStatusIcon,
    getEventIcon,
    formatDuration,
    detailsContainerRef,
}) => {
    const eventColorMap = React.useMemo(() => {
        if (!timeline || timeline.length === 0) return {};
        const uniqueEvents = [...new Set(timeline.map((e) => e.event))];
        const map = {};
        uniqueEvents.forEach((name) => {
            map[name] = generateEventColor(name);
        });
        return map;
    }, [timeline]);

    if (!session) {
        return (
            <VStack justify="center" align="center" h="full" spacing={4}>
                <Box as={FiActivity} boxSize={16} color="gray.300" />
                <Text color="gray.500" fontWeight="medium" fontSize="lg">
                    Select a session to view journey
                </Text>
                <Text color="gray.400">Click on any session from the left panel</Text>
            </VStack>
        );
    }

    return (
        <>
            {/* Session Summary Header */}
            <Box p={6} borderBottom="1px" borderColor="gray.200" bgGradient="linear(to-r, blue.50, white)">
                <Flex justify="space-between" align="start" mb={4}>
                    <Box>
                        <Text fontWeight={"semibold"} fontSize={"lg"} mb={2}>Session Journey</Text>
                        <Text as="code" color="gray.600" fontFamily="mono">
                            {session.session_id}
                        </Text>
                    </Box>
                    {session.contact_profile?.is_known ? (
                        <Tag colorScheme="green" display="flex" alignItems="center" size={"md"}>
                            <Box as={FiUserCheck} boxSize={4} mr={1.5} />
                            Known User
                        </Tag>
                    ) : (
                        <Tag colorScheme="gray" display="flex" alignItems="center" size={"md"}>
                            <Box as={FiUser} boxSize={4} mr={1.5} />
                            Anonymous User
                        </Tag>
                    )}
                </Flex>

                <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
                    <GridItem>
                        <Text color="gray.500" mb={1}>Contact Number</Text>
                        <Text fontWeight="500">
                            {session.contact || <Text as="span" color="gray.400">N/A</Text>}
                        </Text>
                    </GridItem>
                    <GridItem>
                        <Text color="gray.500" mb={1}>Total Events</Text>
                        <Text fontWeight="500">{session.total_events}</Text>
                    </GridItem>
                    <GridItem>
                        <Text color="gray.500" mb={1}>Session Duration</Text>
                        <Text fontWeight="500">{formatDuration(session.duration_sec)}</Text>
                    </GridItem>
                    <GridItem>
                        <Text color="gray.500" mb={1}>Status</Text>
                        <HStack>
                            {getStatusIcon(session.status)}
                            <Text fontWeight="500" textTransform="capitalize">{session.status}</Text>
                        </HStack>
                    </GridItem>
                    <GridItem>
                        <Text color="gray.500" mb={1}>Entry Point</Text>
                        <Text fontWeight="500">{session.entry_point}</Text>
                    </GridItem>
                    <GridItem>
                        <Text color="gray.500" mb={1}>Exit Point</Text>
                        <Text fontWeight="500">{session.exit_point}</Text>
                    </GridItem>
                    <GridItem>
                        <Text color="gray.500" mb={1}>Session Start</Text>
                        <Text fontWeight="500">{new Date(session.started_at).toLocaleString()}</Text>
                    </GridItem>
                    <GridItem>
                        <Text color="gray.500" mb={1}>Channel</Text>
                        <Text fontWeight="500">{session.channel === "Unknown" ? <Text as="span" color="gray.400">N/A</Text> : session.channel || "Website"}</Text>
                    </GridItem>
                </Grid>
            </Box>

            {/* Journey Timeline */}
            <Box ref={detailsContainerRef} flex={1} overflowY="auto" p={6}>
                {loading ? (
                    <VStack justify="center" align="center" h="full" spacing={4}>
                        <Spinner size="xl" color="var(--brand-primary)" thickness="4px" />
                        <Text color="gray.500" fontWeight="medium" fontSize="lg">
                            Loading timeline...
                        </Text>
                    </VStack>
                ) : (
                    <Box maxW="4xl" mx="auto">
                        <HStack mb={6} spacing={2}>
                            <Box as={FiActivity} boxSize={5} color="var(--brand-primary)" />
                            <Heading size="md">Journey Timeline</Heading>
                        </HStack>

                        {/* Session Start Marker */}
                        {timeline && timeline.length > 0 && (
                            <Box mb={4}>
                                <Tag bg="var(--brand-primary)" color="white" display="flex" alignItems="center" width="fit-content">
                                    <Box as={FiPlayCircle} boxSize={3} mr={1} />
                                    Session Start
                                </Tag>
                            </Box>
                        )}

                        <VStack spacing={4} align="stretch">
                            {timeline && timeline.length > 0 ? (
                                timeline.map((event, index) => {
                                    const colors = eventColorMap[event.event] || {
                                        bg: 'gray.50',
                                        border: 'gray.200',
                                        text: 'gray.700',
                                    };
                                    return (
                                        <EventTimelineCard
                                            key={index}
                                            event={event}
                                            index={index}
                                            totalEvents={timeline.length}
                                            isHovered={hoveredEvent === index}
                                            onHover={() => onEventHover(index)}
                                            onHoverEnd={() => onEventHover(null)}
                                            getEventIcon={getEventIcon}
                                            colors={colors}
                                        />
                                    );
                                })
                            ) : (
                                <Text color="gray.500" textAlign="center" py={8}>
                                    No events found for this session
                                </Text>
                            )}
                        </VStack>

                        {/* Session End Marker */}
                        {timeline && timeline.length > 0 && (
                            <Box mt={4}>
                                <Tag bg="gray.600" color="white" display="flex" alignItems="center" width="fit-content">
                                    <Box as={FiStopCircle} boxSize={3} mr={1} />
                                    Session End
                                </Tag>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
export default function SessionJourneyExplorer() {
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [sessionDetails, setSessionDetails] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState('all');
    const [timeFilter, setTimeFilter] = useState('24');
    const [channelFilter, setChannelFilter] = useState('all');
    const [hoveredEvent, setHoveredEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

    const { loading: permissionsLoading, can } = usePagePermissions('analytics/session-journey');

    const sessionJourneyPermission = can("session-journey");

    const detailsContainerRef = useRef(null);

    // Debounced search query — fires 500ms after user stops typing
    const debouncedSearch = useDebounce(searchQuery, 500);

    // Deep-link: /analytics/session-journey?session_id=sess_… auto-selects the
    // session (Session Analyzer trigger drawer links here). Search bypasses
    // the time window server-side for sess_-prefixed ids.
    useEffect(() => {
        if (permissionsLoading || !sessionJourneyPermission) return;
        const sid = new URLSearchParams(window.location.search).get('session_id');
        if (sid) {
            setSearchQuery(sid);
            setSelectedSession({ session_id: sid });
        }
    }, [permissionsLoading, sessionJourneyPermission]);

    const getEventIcon = (eventType) => {
        const type = eventType?.toLowerCase() || '';
        if (type.includes('page') || type.includes('view')) return FiEye;
        if (type.includes('click') || type.includes('action')) return FiMousePointer;
        if (type.includes('login')) return FiLogIn;
        if (type.includes('purchase')) return FiShoppingCart;
        if (type.includes('exit') || type.includes('end')) return FiLogOut;
        return FiActivity;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <Box as={FiCheckCircle} color="green.500" boxSize={3} />;
            case 'partial':
                return <Box as={FiAlertCircle} color="yellow.500" boxSize={3} />;
            case 'dropped':
                return <Box as={FiXCircle} color="red.500" boxSize={3} />;
            default:
                return <Box as={FiActivity} color="gray.500" boxSize={3} />;
        }
    };

    const formatDuration = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    // Fetch sessions — uses debouncedSearch instead of raw searchQuery
    const fetchSessions = useCallback(async () => {
        setLoading(true);
        try {
            const channelParam = channelFilter !== 'all' ? `&channel=${channelFilter}` : '';
            const url = `${process.env.REACT_APP_API_URL}/journeys/sessions?hours=${timeFilter}&type=${userTypeFilter}&page=${pagination.page}&limit=${pagination.limit}&search=${debouncedSearch}${channelParam}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                setSessions(response.data.data.sessions || []);
                setPagination(response.data.data.pagination);
            } else {
                throw new Error('Failed to fetch sessions');
            }
        } catch (error) {
            console.error('Error fetching sessions:', error);
            setSessions([]);
        } finally {
            setLoading(false);
        }
    }, [timeFilter, userTypeFilter, pagination.page, pagination.limit, debouncedSearch, channelFilter]);

    const fetchSessionDetails = useCallback(async (sessionId) => {
        setDetailsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/journeys/sessions/${sessionId}?hours=${timeFilter}&limit=300`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                setSessionDetails(response.data.data.session);
                setTimeline(response.data.data.timeline || []);
            } else {
                throw new Error('Failed to fetch session details');
            }
        } catch (error) {
            console.error('Error fetching session details:', error);
            setSessionDetails(null);
            setTimeline([]);
        } finally {
            setDetailsLoading(false);
        }
    }, [timeFilter]);

    useEffect(() => {
        if (permissionsLoading || !can('session-journey')) return;
        fetchSessions();
    }, [permissionsLoading, can, fetchSessions]);

    useEffect(() => {
        if (selectedSession) {
            fetchSessionDetails(selectedSession.session_id);
            if (detailsContainerRef.current) {
                detailsContainerRef.current.scrollTop = 0;
            }
        }
    }, [selectedSession, fetchSessionDetails]);

    const handlePageChange = (pageNumber) => {
        setPagination((prev) => ({ ...prev, page: pageNumber }));
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const handleUserTypeChange = (type) => {
        setUserTypeFilter(type);
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const handleTimeFilterChange = (hours) => {
        setTimeFilter(hours);
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const handleChannelFilterChange = (channel) => {
        setChannelFilter(channel);
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    if (permissionsLoading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header />
            <BoxWrapper>
                {sessionJourneyPermission ? (
                <Flex gap={4}>
                    <SessionList
                        sessions={sessions}
                        selectedSession={selectedSession}
                        onSelectSession={setSelectedSession}
                        searchQuery={searchQuery}
                        onSearchChange={handleSearchChange}
                        userTypeFilter={userTypeFilter}
                        onUserTypeChange={handleUserTypeChange}
                        timeFilter={timeFilter}
                        onTimeFilterChange={handleTimeFilterChange}
                        channelFilter={channelFilter}
                        onChannelFilterChange={handleChannelFilterChange}
                        getStatusIcon={getStatusIcon}
                        loading={loading}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                    />

                    <Box
                        flex={1}
                        bg="white"
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="lg"
                        boxShadow="sm"
                        overflow="hidden"
                        display="flex"
                        flexDirection="column"
                    >
                        <SessionDetails
                            session={sessionDetails}
                            timeline={timeline}
                            loading={detailsLoading}
                            hoveredEvent={hoveredEvent}
                            onEventHover={setHoveredEvent}
                            getStatusIcon={getStatusIcon}
                            getEventIcon={getEventIcon}
                            formatDuration={formatDuration}
                            detailsContainerRef={detailsContainerRef}
                        />
                    </Box>
                </Flex>
                ) : (
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                )}
            </BoxWrapper>
        </>
    );
}